import { useState, useRef, useCallback, useEffect } from 'react';
import { Modal } from './Modal';
import {
  importFromFile,
  importFromSwarm,
  importFromPrefetched,
  prefetchFromSwarm,
  validateKeystoreFile,
  hasExistingBackup,
  BackupProgress,
  PrefetchedBackup,
} from '../services/account/backup';
import { getAccount, saveAccount, setActiveAccountSubdomain } from '../services/storage';
import type { Account } from '../types';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: (account: Account) => void;
}

type ImportTab = 'file' | 'swarm';

const progressLabels: Record<BackupProgress, string> = {
  'deriving-key': 'Deriving encryption key...',
  'encrypting': 'Encrypting...',
  'decrypting': 'Decrypting account data...',
  'verifying': 'Verifying password...',
  'uploading': 'Uploading...',
  'downloading': 'Downloading from Swarm...',
  'looking-up-ens': 'Looking up ENS record...',
  'updating-ens': 'Updating ENS...',
};

export function ImportModal({ isOpen, onClose, onImportSuccess }: ImportModalProps) {
  const [activeTab, setActiveTab] = useState<ImportTab>('file');
  const [password, setPassword] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [progress, setProgress] = useState<BackupProgress | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  // File input state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileSubdomain, setFileSubdomain] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ENS backup check state
  const [ensBackupExists, setEnsBackupExists] = useState<boolean | null>(null);
  const [isCheckingEns, setIsCheckingEns] = useState(false);

  // Pre-fetched backup state (for faster import)
  const [prefetchedBackup, setPrefetchedBackup] = useState<PrefetchedBackup | null>(null);
  const [prefetchSubdomain, setPrefetchSubdomain] = useState<string | null>(null);
  const [isPrefetching, setIsPrefetching] = useState(false);

  // Debounced ENS backup check
  useEffect(() => {
    if (activeTab !== 'swarm' || !subdomain.trim()) {
      setEnsBackupExists(null);
      return;
    }

    const trimmedSubdomain = subdomain.trim().toLowerCase();
    if (trimmedSubdomain.length < 2) {
      setEnsBackupExists(null);
      return;
    }

    setIsCheckingEns(true);
    const timeoutId = setTimeout(async () => {
      try {
        const exists = await hasExistingBackup(trimmedSubdomain);
        setEnsBackupExists(exists);
      } catch {
        setEnsBackupExists(null);
      } finally {
        setIsCheckingEns(false);
      }
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(timeoutId);
      setIsCheckingEns(false);
    };
  }, [subdomain, activeTab]);

  // Pre-fetch ENS backup hash when backup is found (skips ENS lookup during import)
  useEffect(() => {
    if (activeTab !== 'swarm' || !ensBackupExists || isPrefetching || isImporting) {
      return;
    }

    const trimmedSubdomain = subdomain.trim().toLowerCase();

    // Skip if already prefetched for this subdomain
    if (prefetchSubdomain === trimmedSubdomain && prefetchedBackup) {
      return;
    }

    // Clear old prefetch if subdomain changed
    if (prefetchSubdomain !== trimmedSubdomain) {
      setPrefetchedBackup(null);
      setPrefetchSubdomain(null);
    }

    // Start prefetching (caches ENS lookup result)
    setIsPrefetching(true);
    prefetchFromSwarm(trimmedSubdomain)
      .then(data => {
        setPrefetchedBackup(data);
        setPrefetchSubdomain(trimmedSubdomain);
      })
      .catch(err => {
        // Silent fail - will fall back to regular import
        console.warn('Prefetch failed:', err.message);
      })
      .finally(() => {
        setIsPrefetching(false);
      });
  }, [subdomain, activeTab, ensBackupExists, isPrefetching, isImporting, prefetchSubdomain, prefetchedBackup]);

  const handleClose = () => {
    // Reset state when closing
    setPassword('');
    setSubdomain('');
    setError('');
    setWarning('');
    setProgress(null);
    setSelectedFile(null);
    setFileSubdomain(null);
    setPrefetchedBackup(null);
    setPrefetchSubdomain(null);
    onClose();
  };

  const handleFileSelect = useCallback(async (file: File) => {
    setError('');
    setWarning('');

    // Validate the file
    const validation = await validateKeystoreFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid keystore file');
      return;
    }

    setSelectedFile(file);
    setFileSubdomain(validation.subdomain || null);
  }, []);

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const checkExistingAccount = (subdomain: string): { exists: boolean; canUpdate: boolean; error?: string } => {
    const existing = getAccount(subdomain);
    if (!existing) {
      return { exists: false, canUpdate: true };
    }
    // For now, allow overwriting (we could add more sophisticated checks)
    return { exists: true, canUpdate: true };
  };

  const handleImportFromFile = async () => {
    if (!selectedFile) {
      setError('Please select a keystore file');
      return;
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    setError('');
    setWarning('');
    setIsImporting(true);
    setProgress(null);

    try {
      // Check if account already exists
      if (fileSubdomain) {
        const check = checkExistingAccount(fileSubdomain);
        if (check.exists && !check.canUpdate) {
          setError(check.error || 'Account already exists');
          setIsImporting(false);
          return;
        }
      }

      const result = await importFromFile(selectedFile, password, {
        onProgress: setProgress,
      });

      // Show warning if there was one
      if (result.warning) {
        setWarning(result.warning);
      }

      // Save the imported account
      saveAccount(result.account);
      setActiveAccountSubdomain(result.account.subdomain);

      // Notify parent of success
      onImportSuccess(result.account);
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed';
      if (message.includes('Incorrect password')) {
        setError('Incorrect password');
      } else {
        setError(message);
      }
    } finally {
      setIsImporting(false);
      setProgress(null);
    }
  };

  const handleImportFromSwarm = async () => {
    if (!subdomain.trim()) {
      setError('Please enter your username');
      return;
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    setError('');
    setWarning('');
    setIsImporting(true);
    setProgress(null);

    const trimmedSubdomain = subdomain.trim().toLowerCase();

    try {
      // Check if account already exists
      const check = checkExistingAccount(trimmedSubdomain);
      if (check.exists && !check.canUpdate) {
        setError(check.error || 'Account already exists');
        setIsImporting(false);
        return;
      }

      let result;

      // Use pre-fetched data if available for this subdomain
      if (prefetchedBackup && prefetchSubdomain === trimmedSubdomain) {
        result = await importFromPrefetched(prefetchedBackup, trimmedSubdomain, password, {
          onProgress: setProgress,
        });
      } else {
        // Fall back to regular import (downloads from Swarm)
        result = await importFromSwarm(trimmedSubdomain, password, {
          onProgress: setProgress,
        });
      }

      // Show warning if there was one
      if (result.warning) {
        setWarning(result.warning);
      }

      // Save the imported account
      saveAccount(result.account);
      setActiveAccountSubdomain(result.account.subdomain);

      // Notify parent of success
      onImportSuccess(result.account);
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed';
      if (message.includes('Incorrect password')) {
        setError('Incorrect password');
      } else if (message.includes('No backup found')) {
        setError('No backup found for this account. Try importing from a file instead.');
      } else {
        setError(message);
      }
    } finally {
      setIsImporting(false);
      setProgress(null);
    }
  };

  const handleImport = () => {
    if (activeTab === 'file') {
      handleImportFromFile();
    } else {
      handleImportFromSwarm();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Account">
      <div className="export-modal">
        {/* Tabs */}
        <div className="export-tabs">
          <button
            className={`export-tab ${activeTab === 'file' ? 'active' : ''}`}
            onClick={() => setActiveTab('file')}
            disabled={isImporting}
          >
            From File
          </button>
          <button
            className={`export-tab ${activeTab === 'swarm' ? 'active' : ''}`}
            onClick={() => setActiveTab('swarm')}
            disabled={isImporting}
          >
            From Swarm
          </button>
        </div>

        {/* Content */}
        <div className="export-content">
          {error && <div className="modal-error">{error}</div>}
          {warning && <div className="export-warning">{warning}</div>}

          {activeTab === 'file' ? (
            <>
              {/* File drop zone or selected file */}
              {!selectedFile ? (
                <div
                  className="import-dropzone"
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleFileDrop}
                  onDragOver={e => e.preventDefault()}
                >
                  <div className="import-dropzone-icon">&#128196;</div>
                  <div className="import-dropzone-text">
                    Drop keystore file here or click to browse
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,application/json"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                </div>
              ) : (
                <div className="import-file-info">
                  <span className="import-file-icon">&#128196;</span>
                  <div className="import-file-details">
                    <div className="import-file-name">{selectedFile.name}</div>
                    {fileSubdomain && (
                      <div className="import-file-subdomain">{fileSubdomain}.fairdrop.eth</div>
                    )}
                  </div>
                  <button
                    className="import-file-remove"
                    onClick={() => {
                      setSelectedFile(null);
                      setFileSubdomain(null);
                    }}
                    disabled={isImporting}
                  >
                    &times;
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="export-description">
                Enter your username to restore from a cloud backup. The backup must have been
                previously exported to Swarm.
              </p>
              <div className="import-subdomain-wrapper">
                <input
                  type="text"
                  placeholder="Username (e.g., alice)"
                  value={subdomain}
                  onChange={e => setSubdomain(e.target.value)}
                  className="modal-input"
                  disabled={isImporting}
                />
                <span className="import-subdomain-status">
                  {isCheckingEns && <span className="import-status-checking">...</span>}
                  {!isCheckingEns && ensBackupExists === true && (
                    <span className="import-status-found" title="Backup found">✓</span>
                  )}
                  {!isCheckingEns && ensBackupExists === false && subdomain.trim().length >= 2 && (
                    <span className="import-status-not-found" title="No backup found">✗</span>
                  )}
                </span>
              </div>
            </>
          )}

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="modal-input"
            disabled={isImporting}
            onKeyDown={e => {
              if (e.key === 'Enter' && !isImporting) {
                handleImport();
              }
            }}
          />

          {progress && (
            <div className="export-progress">
              <div className="export-progress-spinner" />
              <span>{progressLabels[progress]}</span>
            </div>
          )}

          <div className="modal-actions">
            <button className="btn btn-outline" onClick={handleClose} disabled={isImporting}>
              Cancel
            </button>
            <button
              className="btn btn-white"
              onClick={handleImport}
              disabled={
                isImporting ||
                !password ||
                (activeTab === 'file' && !selectedFile) ||
                (activeTab === 'swarm' && !subdomain.trim())
              }
            >
              {isImporting ? 'Importing...' : 'Import Account'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
